/**
 * window.fetch'i bir kez sarar: kendi API'lerimize giden ve başarısız dönen
 * istekleri bildirir. API çağrısı yapan dosyaların çoğu (2026-09-21'de 148
 * dosyanın 99'u) fetch'i doğrudan çağırıyor, apiClient.ts'i değil; hepsini
 * görmenin tek yolu fetch'in kendisi.
 *
 * Bildirilmez: 401 (apiClient zaten girişe yönlendiriyor), iptal edilen
 * istekler (AbortError), tarayıcı çevrimdışıyken ağ hataları, rapor ucunun
 * kendisi (döngü olmasın), başka sitelere giden istekler.
 * İstek ve yanıt gövdesi rapora girmez; yalnız sunucunun hata mesajı.
 */

import type { ReportInput } from './types';

export interface FetchMonitorOptions {
  isOwnApi: (url: string) => boolean;
  isReportUrl: (url: string) => boolean;
  report: (input: ReportInput) => void;
}

function requestInfo(input: RequestInfo | URL, init?: RequestInit): { url: string; method: string } {
  if (typeof input === 'string') return { url: input, method: (init?.method || 'GET').toUpperCase() };
  if (input instanceof URL) return { url: input.toString(), method: (init?.method || 'GET').toUpperCase() };
  return { url: input.url, method: (init?.method || input.method || 'GET').toUpperCase() };
}

function isAbort(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { name?: unknown }).name === 'AbortError';
}

/** Sunucunun `{error}` / `{message}` alanı; yoksa durum metni. */
async function serverMessage(res: Response): Promise<string> {
  try {
    const body: unknown = await res.clone().json();
    if (body && typeof body === 'object') {
      const fields = body as Record<string, unknown>;
      const message = fields.error ?? fields.message;
      if (typeof message === 'string') return message.slice(0, 300);
    }
  } catch {
    /* JSON değil */
  }
  return res.statusText || '';
}

/** Kökeni atılmış yol + sorgu: `/api/v2/requests/42?tab=offers`. */
function pathOf(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

export function wrapFetch(original: typeof fetch, options: FetchMonitorOptions): typeof fetch {
  return async function monitoredFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const { url, method } = requestInfo(input, init);
    const watched = options.isOwnApi(url) && !options.isReportUrl(url);
    try {
      const res = await original(input, init);
      if (watched && !res.ok && res.status !== 401) {
        void serverMessage(res).then((message) =>
          options.report({ kind: 'api', status: res.status, method, endpoint: pathOf(url), message }),
        );
      }
      return res;
    } catch (err) {
      if (watched && !isAbort(err) && navigator.onLine !== false) {
        const message = err instanceof Error ? err.message : String(err);
        options.report({ kind: 'network', method, endpoint: pathOf(url), message });
      }
      throw err;
    }
  };
}
